import { useEffect, useState, useCallback, useRef } from "react";
import {
  getAllProjects, getProject, createProject, updateProject, deleteProject,
  getProjectTasks, getProjectLogs, createLog, updateLog, deleteLog,
  createPlannerItem, updatePlannerItem, updatePlannerStatus, deletePlannerItem,
} from "./ProjectsService";
import { getEpics } from "./EpicsService";
import { getStories } from "./StoriesService";
import { useToast } from "../ToastContext";
import { TaskRow, TaskKanbanBoard, TaskFormModal } from "./TaskViews";
import EpicsTab from "./EpicsTab";
import "./ProjectsPage.css";

// ─── Constants ───────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  "#6366f1","#8b5cf6","#ec4899","#ef4444","#f97316",
  "#eab308","#22c55e","#14b8a6","#3b82f6","#06b6d4",
];

const STATUS_META = {
  PLANNING:  { label: "Planning",   cls: "status-planning"  },
  ACTIVE:    { label: "Active",     cls: "status-active"    },
  ON_HOLD:   { label: "On Hold",    cls: "status-onhold"    },
  COMPLETED: { label: "Completed",  cls: "status-completed" },
  ARCHIVED:  { label: "Archived",   cls: "status-archived"  },
};

const LOG_TYPE_META = {
  UPDATE:   { label: "Update",    icon: "📝", cls: "lt-update"   },
  LEARNING: { label: "Learning",  icon: "💡", cls: "lt-learning" },
  BLOCKER:  { label: "Blocker",   icon: "🚧", cls: "lt-blocker"  },
  MILESTONE:{ label: "Milestone", icon: "🎯", cls: "lt-milestone"},
  RESOURCE: { label: "Resource",  icon: "🔗", cls: "lt-resource" },
  DECISION: { label: "Decision",  icon: "⚖️", cls: "lt-decision" },
};

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtFull(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function daysFrom(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (diff === 0) return "today";
  if (diff > 0) return `in ${diff}d`;
  return `${Math.abs(diff)}d ago`;
}

const KANBAN_COLUMNS = [
  { status: "PLANNING",  label: "Planning",  color: "#6366f1" },
  { status: "ACTIVE",    label: "Active",    color: "#22c55e" },
  { status: "ON_HOLD",   label: "On Hold",   color: "#f59e0b" },
  { status: "COMPLETED", label: "Completed", color: "#3b82f6" },
  { status: "ARCHIVED",  label: "Archived",  color: "#6b7280" },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null); // full project detail
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" | "board"
  const [boardSelected, setBoardSelected] = useState(null); // project opened from board

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllProjects();
      setProjects(data || []);
    } catch {
      toast("Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const openProject = async (proj) => {
    try {
      const full = await getProject(proj.id);
      setSelected(full);
    } catch {
      toast("Failed to load project", "error");
    }
  };

  const openProjectFromBoard = async (proj) => {
    try {
      const full = await getProject(proj.id);
      setBoardSelected(full);
    } catch {
      toast("Failed to load project", "error");
    }
  };

  const handleBoardStatusChange = async (projectId, newStatus) => {
    const proj = projects.find(p => p.id === projectId);
    if (!proj || proj.status === newStatus) return;
    // Optimistic update
    setProjects(ps => ps.map(p => p.id === projectId ? { ...p, status: newStatus } : p));
    if (boardSelected?.id === projectId) setBoardSelected(b => b ? { ...b, status: newStatus } : b);
    try {
      await updateProject(projectId, { ...proj, status: newStatus });
    } catch {
      toast("Failed to update status", "error");
      loadProjects(); // revert
    }
  };

  const handleSaveProject = async (formData) => {
    try {
      if (editingProject) {
        const updated = await updateProject(editingProject.id, formData);
        setProjects(ps => ps.map(p => p.id === updated.id ? updated : p));
        if (selected?.id === updated.id) setSelected(updated);
        toast("Project updated", "success");
      } else {
        const created = await createProject(formData);
        setProjects(ps => [created, ...ps]);
        toast("Project created", "success");
      }
      setShowForm(false);
      setEditingProject(null);
    } catch {
      toast("Failed to save project", "error");
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await deleteProject(id);
      setProjects(ps => ps.filter(p => p.id !== id));
      if (selected?.id === id) setSelected(null);
      toast("Project deleted", "success");
    } catch {
      toast("Failed to delete project", "error");
    }
  };

  const filtered = projects.filter(p => filter === "ALL" || p.status === filter);

  return (
    <div className={`proj-page ${viewMode === "board" ? "proj-page-board" : ""}`}>

      {viewMode === "list" ? (
        <>
          {/* Sidebar */}
          <aside className="proj-sidebar">
            <div className="proj-sidebar-header">
              <h2>Projects</h2>
              <div className="proj-header-actions">
                <div className="proj-view-toggle">
                  <button className={`proj-view-btn ${viewMode === "list" ? "active" : ""}`}
                          onClick={() => setViewMode("list")} title="List view">☰</button>
                  <button className={`proj-view-btn ${viewMode === "board" ? "active" : ""}`}
                          onClick={() => setViewMode("board")} title="Board view">⊞</button>
                </div>
                <button className="proj-new-btn" onClick={() => { setEditingProject(null); setShowForm(true); }}>
                  + New
                </button>
              </div>
            </div>

            <div className="proj-filter-row">
              {["ALL","ACTIVE","PLANNING","ON_HOLD","COMPLETED"].map(f => (
                <button key={f} className={`proj-filter-btn ${filter === f ? "active" : ""}`}
                        onClick={() => setFilter(f)}>
                  {f === "ON_HOLD" ? "Hold" : f.charAt(0) + f.slice(1).toLowerCase()}
                </button>
              ))}
            </div>

            {loading && <div className="proj-loading">Loading…</div>}

            <div className="proj-list">
              {!loading && filtered.length === 0 && (
                <div className="proj-empty">
                  {filter === "ALL" ? "No projects yet. Create your first one!" : `No ${filter.toLowerCase()} projects.`}
                </div>
              )}
              {filtered.map(p => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  active={selected?.id === p.id}
                  onClick={() => openProject(p)}
                />
              ))}
            </div>
          </aside>

          {/* Detail Panel */}
          <main className="proj-detail-panel">
            {selected ? (
              <ProjectDetail
                project={selected}
                onEdit={() => { setEditingProject(selected); setShowForm(true); }}
                onDelete={() => handleDeleteProject(selected.id)}
                onRefresh={async () => { const full = await getProject(selected.id); setSelected(full); }}
                toast={toast}
              />
            ) : (
              <div className="proj-empty-detail">
                <div className="proj-empty-detail-icon">📁</div>
                <div className="proj-empty-detail-title">Select a project</div>
                <div className="proj-empty-detail-sub">
                  Pick a project from the left, or create a new one to start tracking.
                </div>
              </div>
            )}
          </main>
        </>
      ) : (
        /* Board View */
        <div className="proj-board-wrap">
          <div className="proj-board-topbar">
            <div className="proj-board-topbar-left">
              <span className="proj-board-title">Projects Board</span>
              <span className="proj-board-count">{projects.length} projects</span>
            </div>
            <div className="proj-board-topbar-right">
              <div className="proj-view-toggle">
                <button className={`proj-view-btn ${viewMode === "list" ? "active" : ""}`}
                        onClick={() => setViewMode("list")} title="List view">☰</button>
                <button className={`proj-view-btn ${viewMode === "board" ? "active" : ""}`}
                        onClick={() => setViewMode("board")} title="Board view">⊞</button>
              </div>
              <button className="proj-new-btn" onClick={() => { setEditingProject(null); setShowForm(true); }}>
                + New Project
              </button>
            </div>
          </div>

          {loading ? (
            <div className="proj-loading proj-board-loading">Loading projects…</div>
          ) : (
            <KanbanBoard
              projects={projects}
              onCardClick={openProjectFromBoard}
              onStatusChange={handleBoardStatusChange}
            />
          )}
        </div>
      )}

      {/* Project Form Modal */}
      {showForm && (
        <ProjectFormModal
          project={editingProject}
          onSave={handleSaveProject}
          onClose={() => { setShowForm(false); setEditingProject(null); }}
        />
      )}

      {/* Board project detail modal */}
      {boardSelected && (
        <div className="proj-modal-overlay proj-board-detail-overlay"
             onClick={e => e.target === e.currentTarget && setBoardSelected(null)}>
          <div className="proj-board-detail-modal">
            <button className="proj-board-detail-close" onClick={() => setBoardSelected(null)}>✕</button>
            <ProjectDetail
              project={boardSelected}
              onEdit={() => { setEditingProject(boardSelected); setBoardSelected(null); setShowForm(true); }}
              onDelete={() => { handleDeleteProject(boardSelected.id); setBoardSelected(null); }}
              onRefresh={async () => {
                const full = await getProject(boardSelected.id);
                setBoardSelected(full);
                setProjects(ps => ps.map(p => p.id === full.id ? full : p));
              }}
              toast={toast}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kanban Board ─────────────────────────────────────────────────────────────

function KanbanCard({ project: p, onDragStart, onClick }) {
  const pct = p.totalTasks > 0 ? Math.round((p.doneTasks / p.totalTasks) * 100) : null;
  return (
    <div
      className="kb-card"
      style={{ "--card-color": p.color || "#6366f1" }}
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = "move"; onDragStart(p.id); }}
      onClick={onClick}
    >
      <div className="kb-card-accent" />
      <div className="kb-card-body">
        <div className="kb-card-top">
          <span className="kb-card-icon">{p.icon || "📁"}</span>
          <span className="kb-card-name">{p.name}</span>
        </div>
        {p.category && <div className="kb-card-category">{p.category}</div>}
        {pct !== null && (
          <div className="kb-progress-wrap">
            <div className="kb-progress-bar">
              <div className="kb-progress-fill" style={{ width: pct + "%" }} />
            </div>
            <span className="kb-progress-label">{pct}%</span>
          </div>
        )}
        <div className="kb-card-footer">
          {p.overdueTasks > 0 && (
            <span className="kb-chip kb-chip-overdue">⚠ {p.overdueTasks} overdue</span>
          )}
          {p.totalLogs > 0 && (
            <span className="kb-chip">📝 {p.totalLogs}</span>
          )}
          {p.targetDate && (
            <span className="kb-chip">🗓 {fmt(p.targetDate)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function KanbanBoard({ projects, onCardClick, onStatusChange }) {
  const draggingId = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  return (
    <div className="kb-board">
      {KANBAN_COLUMNS.map(col => {
        const colProjects = projects.filter(p => p.status === col.status);
        return (
          <div
            key={col.status}
            className={`kb-col ${dragOverCol === col.status ? "kb-col-drag-over" : ""}`}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCol(col.status); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
            onDrop={e => {
              e.preventDefault();
              setDragOverCol(null);
              if (draggingId.current) {
                onStatusChange(draggingId.current, col.status);
                draggingId.current = null;
              }
            }}
          >
            <div className="kb-col-header" style={{ "--col-color": col.color }}>
              <span className="kb-col-dot" />
              <span className="kb-col-label">{col.label}</span>
              <span className="kb-col-count">{colProjects.length}</span>
            </div>
            <div className="kb-col-body">
              {colProjects.length === 0 && (
                <div className="kb-col-empty">Drop here</div>
              )}
              {colProjects.map(p => (
                <KanbanCard
                  key={p.id}
                  project={p}
                  onDragStart={id => { draggingId.current = id; }}
                  onClick={() => onCardClick(p)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Project Card (sidebar) ───────────────────────────────────────────────────

function ProjectCard({ project: p, active, onClick }) {
  const sm = STATUS_META[p.status] || STATUS_META.PLANNING;
  const pct = p.totalTasks > 0 ? Math.round((p.doneTasks / p.totalTasks) * 100) : null;
  const goalsDone = (p.goals || []).filter(g => g.done).length;
  const goalsTotal = (p.goals || []).length;

  return (
    <div className={`proj-card ${active ? "proj-card-active" : ""}`}
         style={{ "--card-color": p.color || "#6366f1" }}
         onClick={onClick}>
      <div className="proj-card-accent" />
      <div className="proj-card-body">
        <div className="proj-card-top-row">
          <span className="proj-card-icon">{p.icon || "📁"}</span>
          <span className="proj-card-name">{p.name}</span>
          <span className={`proj-status-badge ${sm.cls}`}>{sm.label}</span>
        </div>
        {p.category && <div className="proj-card-category">{p.category}</div>}
        <div className="proj-card-stats-row">
          {pct !== null && (
            <span className="proj-stat">{p.doneTasks}/{p.totalTasks} tasks</span>
          )}
          {p.overdueTasks > 0 && (
            <span className="proj-stat proj-stat-overdue">⚠ {p.overdueTasks} overdue</span>
          )}
          {goalsTotal > 0 && (
            <span className="proj-stat">🎯 {goalsDone}/{goalsTotal} goals</span>
          )}
        </div>
        {pct !== null && (
          <div className="proj-mini-progress">
            <div className="proj-mini-progress-fill" style={{ width: pct + "%" }} />
          </div>
        )}
        {p.targetDate && (
          <div className="proj-card-target">
            🗓 {fmt(p.targetDate)} <span className="proj-days-from">({daysFrom(p.targetDate)})</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Project Detail ───────────────────────────────────────────────────────────

function ProjectDetail({ project: p, onEdit, onDelete, onRefresh, toast }) {
  const [tab, setTab] = useState("log");
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [epicsForPicker, setEpicsForPicker] = useState([]);
  const [epicsForPickerLoaded, setEpicsForPickerLoaded] = useState(false);
  const [goals, setGoals] = useState(p.goals || []);
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskView, setTaskView] = useState("list"); // "list" | "board"

  // Drag-and-drop refs (no re-render needed during drag)
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    setGoals(p.goals || []);
    setTasksLoaded(false);
    setLogsLoaded(false);
    setEpicsForPickerLoaded(false);
    setTab("log");
  }, [p.id]);

  useEffect(() => {
    if (tab === "log" && !logsLoaded) {
      getProjectLogs(p.id).then(data => { setLogs(data || []); setLogsLoaded(true); }).catch(() => {});
    }
    if (tab === "tasks" && !tasksLoaded) {
      getProjectTasks(p.id).then(data => { setTasks(data || []); setTasksLoaded(true); }).catch(() => {});
    }
    if (tab === "tasks" && !epicsForPickerLoaded) {
      // Batch: epics for the project, then each epic's stories — feeds the
      // Epic/Story picker in TaskFormModal so existing tasks can be moved
      // into the hierarchy. Small N (epics per project), so N+1 here is fine
      // client-side, unlike the batched server-side stats rollups.
      getEpics(p.id)
        .then(epicsList => Promise.all(
          epicsList.map(ep => getStories(ep.id).then(stories => ({ ...ep, stories })).catch(() => ({ ...ep, stories: [] })))
        ))
        .then(setEpicsForPicker)
        .catch(() => {})
        .finally(() => setEpicsForPickerLoaded(true));
    }
  }, [tab, p.id, logsLoaded, tasksLoaded, epicsForPickerLoaded]);

  // storyId -> { epicName, storyName } lookup, feeds the 🧩/📖 badge on TaskRow
  const storyLookup = {};
  epicsForPicker.forEach(ep => (ep.stories || []).forEach(s => {
    storyLookup[s.id] = { epicName: ep.name, storyName: s.name };
  }));

  const sm = STATUS_META[p.status] || STATUS_META.PLANNING;
  const goalsDone = goals.filter(g => g.done).length;
  const goalsPct = goals.length > 0 ? Math.round((goalsDone / goals.length) * 100) : 0;
  const taskPct = p.totalTasks > 0 ? Math.round((p.doneTasks / p.totalTasks) * 100) : 0;

  const toggleGoal = async (goalId) => {
    const updated = goals.map(g => g.id === goalId ? { ...g, done: !g.done } : g);
    setGoals(updated);
    try {
      await updateProject(p.id, { goals: updated });
      onRefresh();
    } catch { toast("Failed to save goal", "error"); }
  };

  const handleSaveLog = async (data) => {
    try {
      if (editingLog) {
        const updated = await updateLog(p.id, editingLog.id, data);
        setLogs(ls => ls.map(l => l.id === updated.id ? updated : l));
        toast("Entry updated", "success");
      } else {
        const created = await createLog(p.id, data);
        setLogs(ls => [created, ...ls]);
        toast("Entry added", "success");
      }
      setShowLogForm(false);
      setEditingLog(null);
    } catch { toast("Failed to save entry", "error"); }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Delete this log entry?")) return;
    try {
      await deleteLog(p.id, logId);
      setLogs(ls => ls.filter(l => l.id !== logId));
      toast("Entry deleted", "success");
    } catch { toast("Failed to delete entry", "error"); }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    try {
      const updated = await updatePlannerStatus(taskId, newStatus);
      setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
      onRefresh();
    } catch {
      toast("Failed to update task", "error");
      getProjectTasks(p.id).then(data => setTasks(data || [])).catch(() => {});
    }
  };

  const handleSaveTask = async (data) => {
    try {
      if (editingTask) {
        const updated = await updatePlannerItem(editingTask.id, { ...editingTask, ...data });
        setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
        setEditingTask(null);
        setShowTaskForm(false);
        toast("Task updated", "success");
      } else {
        const created = await createPlannerItem({ ...data, projectId: p.id, projectName: p.name, type: "TODO" });
        setTasks(ts => [...ts, created]);
        setShowTaskForm(false);
        onRefresh();
        toast("Task added", "success");
      }
    } catch { toast(editingTask ? "Failed to update task" : "Failed to add task", "error"); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await deletePlannerItem(taskId);
      setTasks(ts => ts.filter(t => t.id !== taskId));
      onRefresh();
      toast("Task deleted", "success");
    } catch { toast("Failed to delete task", "error"); }
  };

  const handleDragStart = (index) => { dragIndex.current = index; };
  const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
  const handleDrop = (e, index) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === index) { setDragOverIndex(null); return; }
    setTasks(ts => {
      const next = [...ts];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
    dragIndex.current = null;
    setDragOverIndex(null);
  };
  const handleDragEnd = () => { dragIndex.current = null; setDragOverIndex(null); };

  return (
    <div className="proj-detail">
      {/* Header */}
      <div className="proj-detail-header" style={{ "--proj-color": p.color || "#6366f1" }}>
        <div className="proj-detail-title-row">
          <span className="proj-detail-icon">{p.icon || "📁"}</span>
          <h1 className="proj-detail-name">{p.name}</h1>
          <span className={`proj-status-badge ${sm.cls}`}>{sm.label}</span>
          <div className="proj-detail-actions">
            <button className="proj-action-icon" onClick={onEdit} title="Edit project">✏️</button>
            <button className="proj-action-icon" onClick={onDelete} title="Delete project">🗑</button>
          </div>
        </div>
        {p.category && <div className="proj-detail-category">📂 {p.category}</div>}

        {/* Meta dates row */}
        <div className="proj-detail-meta-row">
          <MetaItem label="Created"   value={fmt(p.createdAt)} />
          <MetaItem label="Updated"   value={fmt(p.updatedAt)} />
          {p.startDate   && <MetaItem label="Started"   value={fmt(p.startDate)} />}
          {p.targetDate  && <MetaItem label="Target"    value={`${fmt(p.targetDate)} (${daysFrom(p.targetDate)})`} />}
          {p.completedDate && <MetaItem label="Completed" value={fmt(p.completedDate)} />}
        </div>
      </div>

      {/* Description */}
      {p.description && (
        <div className="proj-detail-section">
          <p className="proj-detail-description">{p.description}</p>
        </div>
      )}

      {/* Progress overview */}
      <div className="proj-detail-section proj-overview-grid">
        {goals.length > 0 && (
          <div className="proj-overview-card">
            <div className="proj-overview-label">Goals</div>
            <div className="proj-overview-value">{goalsDone} / {goals.length}</div>
            <div className="proj-progress-bar">
              <div className="proj-progress-fill" style={{ width: goalsPct + "%", background: p.color || "#6366f1" }} />
            </div>
            <div className="proj-progress-pct">{goalsPct}%</div>
          </div>
        )}
        {p.totalTasks > 0 && (
          <div className="proj-overview-card">
            <div className="proj-overview-label">Tasks</div>
            <div className="proj-overview-value">{p.doneTasks} / {p.totalTasks}</div>
            <div className="proj-progress-bar">
              <div className="proj-progress-fill" style={{ width: taskPct + "%", background: p.color || "#6366f1" }} />
            </div>
            <div className="proj-progress-pct">{taskPct}%</div>
          </div>
        )}
        <div className="proj-overview-card">
          <div className="proj-overview-label">Log entries</div>
          <div className="proj-overview-value proj-log-count">{p.totalLogs}</div>
        </div>
        {p.overdueTasks > 0 && (
          <div className="proj-overview-card proj-overview-card-warn">
            <div className="proj-overview-label">Overdue tasks</div>
            <div className="proj-overview-value proj-overdue-val">{p.overdueTasks}</div>
          </div>
        )}
      </div>

      {/* Goals checklist */}
      {goals.length > 0 && (
        <div className="proj-detail-section">
          <div className="proj-section-title">Goals</div>
          <div className="proj-goals-list">
            {goals.map(g => (
              <label key={g.id} className={`proj-goal-item ${g.done ? "proj-goal-done" : ""}`}>
                <input type="checkbox" checked={g.done} onChange={() => toggleGoal(g.id)} />
                <span>{g.text}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="proj-tabs">
        {[["log","📝 Log"],["tasks","✅ Tasks"],["epics","🧩 Epics"]].map(([k, label]) => (
          <button key={k} className={`proj-tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
            {label}
            {k === "tasks" && p.totalTasks > 0 && (
              <span className="proj-tab-badge">{p.totalTasks}</span>
            )}
          </button>
        ))}
      </div>

      {/* Log tab */}
      {tab === "log" && (
        <div className="proj-tab-content">
          <div className="proj-tab-toolbar">
            <button className="proj-add-btn" onClick={() => { setEditingLog(null); setShowLogForm(true); }}>
              + Add Entry
            </button>
          </div>
          {!logsLoaded && <div className="proj-loading">Loading log…</div>}
          {logsLoaded && logs.length === 0 && (
            <div className="proj-empty-tab">No log entries yet. Start documenting your progress!</div>
          )}
          <div className="proj-log-timeline">
            {logs.map(l => (
              <LogEntry
                key={l.id}
                log={l}
                projectColor={p.color}
                onEdit={() => { setEditingLog(l); setShowLogForm(true); }}
                onDelete={() => handleDeleteLog(l.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tasks tab */}
      {tab === "tasks" && (
        <div className={`proj-tab-content ${taskView === "board" ? "proj-tab-content-board" : ""}`}>
          <div className="proj-tab-toolbar">
            <button className="proj-add-btn" onClick={() => setShowTaskForm(true)}>
              + Add Task
            </button>
            <div className="proj-task-view-toggle">
              <button
                className={`proj-task-view-btn ${taskView === "list" ? "active" : ""}`}
                onClick={() => setTaskView("list")} title="List view"
              >☰ List</button>
              <button
                className={`proj-task-view-btn ${taskView === "board" ? "active" : ""}`}
                onClick={() => setTaskView("board")} title="Kanban board"
              >⊞ Board</button>
            </div>
          </div>
          {!tasksLoaded && <div className="proj-loading">Loading tasks…</div>}
          {tasksLoaded && tasks.length === 0 && (
            <div className="proj-empty-tab">No tasks linked to this project yet.</div>
          )}
          {tasksLoaded && tasks.length > 0 && taskView === "board" && (
            <TaskKanbanBoard
              tasks={tasks}
              projectColor={p.color}
              onStatusChange={(taskId, newStatus) => handleTaskStatusChange(taskId, newStatus)}
              onEdit={(t) => { setEditingTask(t); setShowTaskForm(true); }}
              onDelete={handleDeleteTask}
            />
          )}
          {tasksLoaded && taskView === "list" && (
            <div className="proj-task-list">
              {tasks.map((t, idx) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  projectColor={p.color}
                  isDragOver={dragOverIndex === idx}
                  storyLabel={t.storyId ? storyLookup[t.storyId] : null}
                  onStatusChange={(s) => handleTaskStatusChange(t.id, s)}
                  onEdit={() => { setEditingTask(t); setShowTaskForm(true); }}
                  onDelete={() => handleDeleteTask(t.id)}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Epics tab */}
      {tab === "epics" && (
        <div className="proj-tab-content">
          <EpicsTab projectId={p.id} projectName={p.name} projectColor={p.color} />
        </div>
      )}

      {/* Log Form Modal */}
      {showLogForm && (
        <LogFormModal
          log={editingLog}
          onSave={handleSaveLog}
          onClose={() => { setShowLogForm(false); setEditingLog(null); }}
        />
      )}

      {/* Task Form Modal — create or edit */}
      {showTaskForm && (
        <TaskFormModal
          projectName={p.name}
          task={editingTask}
          epics={epicsForPicker}
          onSave={handleSaveTask}
          onClose={() => { setShowTaskForm(false); setEditingTask(null); }}
        />
      )}
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div className="proj-meta-item">
      <span className="proj-meta-label">{label}</span>
      <span className="proj-meta-value">{value}</span>
    </div>
  );
}

// ─── Log Entry ────────────────────────────────────────────────────────────────

function LogEntry({ log, projectColor, onEdit, onDelete }) {
  const meta = LOG_TYPE_META[log.type] || LOG_TYPE_META.UPDATE;
  return (
    <div className={`proj-log-entry ${log.pinned ? "proj-log-pinned" : ""}`}
         style={{ "--proj-color": projectColor || "#6366f1" }}>
      <div className="proj-log-line" />
      <div className="proj-log-dot" />
      <div className="proj-log-content">
        <div className="proj-log-header">
          <span className={`proj-log-type ${meta.cls}`}>{meta.icon} {meta.label}</span>
          {log.pinned && <span className="proj-log-pin">📌 Pinned</span>}
          <span className="proj-log-date">{fmtFull(log.createdAt)}</span>
          {log.updatedAt !== log.createdAt && (
            <span className="proj-log-edited">(edited {fmtFull(log.updatedAt)})</span>
          )}
          <div className="proj-log-actions">
            <button className="proj-log-action-btn" onClick={onEdit}>✏️</button>
            <button className="proj-log-action-btn" onClick={onDelete}>🗑</button>
          </div>
        </div>
        <div className="proj-log-text">{log.content}</div>
      </div>
    </div>
  );
}

// ─── Project Form Modal ───────────────────────────────────────────────────────

function ProjectFormModal({ project, onSave, onClose }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name: project?.name || "",
    description: project?.description || "",
    status: project?.status || "PLANNING",
    category: project?.category || "",
    startDate: project?.startDate || "",
    targetDate: project?.targetDate || "",
    completedDate: project?.completedDate || "",
    color: project?.color || PRESET_COLORS[0],
    icon: project?.icon || "📁",
    goals: project?.goals || [],
  });
  const [goalInput, setGoalInput] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addGoal = () => {
    if (!goalInput.trim()) return;
    const goal = { id: "g-" + Date.now(), text: goalInput.trim(), done: false };
    set("goals", [...form.goals, goal]);
    setGoalInput("");
  };

  const removeGoal = (id) => set("goals", form.goals.filter(g => g.id !== id));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      ...form,
      startDate: form.startDate || null,
      targetDate: form.targetDate || null,
      completedDate: form.completedDate || null,
    });
  };

  return (
    <div className="proj-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="proj-modal">
        <div className="proj-modal-header">
          <h3>{isEdit ? "Edit Project" : "New Project"}</h3>
          <button className="proj-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="proj-modal-body" onSubmit={handleSubmit}>
          {/* Color + Icon row */}
          <div className="proj-form-row proj-color-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Icon</label>
              <input className="proj-input proj-icon-input" value={form.icon}
                     onChange={e => set("icon", e.target.value)} placeholder="📁" maxLength={4} />
            </div>
            <div className="proj-form-group" style={{ flex: 3 }}>
              <label>Color</label>
              <div className="proj-color-swatches">
                {PRESET_COLORS.map(c => (
                  <button key={c} type="button"
                          className={`proj-color-swatch ${form.color === c ? "active" : ""}`}
                          style={{ background: c }}
                          onClick={() => set("color", c)} />
                ))}
                <input type="color" className="proj-color-custom" value={form.color}
                       onChange={e => set("color", e.target.value)} title="Custom color" />
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="proj-form-group">
            <label>Name *</label>
            <input className="proj-input" required value={form.name}
                   onChange={e => set("name", e.target.value)} placeholder="e.g. Medicinal Plants Research" />
          </div>

          {/* Description */}
          <div className="proj-form-group">
            <label>Description</label>
            <textarea className="proj-textarea" rows={3} value={form.description}
                      onChange={e => set("description", e.target.value)}
                      placeholder="What is this project about? What do you want to achieve?" />
          </div>

          {/* Status + Category */}
          <div className="proj-form-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Status</label>
              <select className="proj-select" value={form.status} onChange={e => set("status", e.target.value)}>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Category</label>
              <input className="proj-input" value={form.category}
                     onChange={e => set("category", e.target.value)}
                     placeholder="e.g. Trading, Study, Health" />
            </div>
          </div>

          {/* Dates */}
          <div className="proj-form-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Start Date</label>
              <input type="date" className="proj-input" value={form.startDate || ""}
                     onChange={e => set("startDate", e.target.value)} />
            </div>
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Target Date</label>
              <input type="date" className="proj-input" value={form.targetDate || ""}
                     onChange={e => set("targetDate", e.target.value)} />
            </div>
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Completed Date</label>
              <input type="date" className="proj-input" value={form.completedDate || ""}
                     onChange={e => set("completedDate", e.target.value)} />
            </div>
          </div>

          {/* Goals */}
          <div className="proj-form-group">
            <label>Goals / Objectives</label>
            <div className="proj-goals-input-row">
              <input className="proj-input" value={goalInput} onChange={e => setGoalInput(e.target.value)}
                     placeholder="Add a goal and press Enter"
                     onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addGoal())} />
              <button type="button" className="proj-add-goal-btn" onClick={addGoal}>Add</button>
            </div>
            {form.goals.length > 0 && (
              <div className="proj-goals-edit-list">
                {form.goals.map(g => (
                  <div key={g.id} className="proj-goal-edit-item">
                    <span>{g.text}</span>
                    <button type="button" className="proj-goal-remove-btn" onClick={() => removeGoal(g.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="proj-modal-footer">
            <button type="button" className="proj-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="proj-btn-primary"
                    style={{ background: form.color || "#6366f1" }}>
              {isEdit ? "Save Changes" : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Log Form Modal ───────────────────────────────────────────────────────────

function LogFormModal({ log, onSave, onClose }) {
  const [form, setForm] = useState({
    content: log?.content || "",
    type: log?.type || "UPDATE",
    pinned: log?.pinned || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    onSave(form);
  };

  return (
    <div className="proj-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="proj-modal proj-log-modal">
        <div className="proj-modal-header">
          <h3>{log ? "Edit Entry" : "New Log Entry"}</h3>
          <button className="proj-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="proj-modal-body" onSubmit={handleSubmit}>
          <div className="proj-form-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Type</label>
              <select className="proj-select" value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {Object.entries(LOG_TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            <div className="proj-form-group proj-pinned-group">
              <label className="proj-checkbox-label">
                <input type="checkbox" checked={form.pinned}
                       onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))} />
                📌 Pin this entry
              </label>
            </div>
          </div>

          <div className="proj-form-group">
            <label>Content *</label>
            <textarea className="proj-textarea proj-log-textarea" rows={6} required
                      value={form.content}
                      onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                      placeholder={`What ${LOG_TYPE_META[form.type]?.label.toLowerCase() || "update"} would you like to log?`}
                      autoFocus />
          </div>

          <div className="proj-modal-footer">
            <button type="button" className="proj-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="proj-btn-primary">
              {log ? "Save Changes" : "Add Entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

